<!-- @format -->
<template>
    <div class="appointment-request-widget">
        <div class="widget-header">
            <h3>{{ currentStep.title }}</h3>
            <button class="widget-close-button" @click="close">
                <widget-close-svg />
            </button>
        </div>

        <div ref="contentWrapper" class="content-wrapper">
            <div v-if="isLoading" class="loading-container">
                <loading-screen-svg />
            </div>
            <div v-else class="steps-container">
                <appointment-request-details
                    v-if="currentStepIndex === 0"
                    :$v="$v"
                    :appointment-request="appointmentRequest"
                    :finance-values="financeValues"
                    :subtotal="subtotal"
                    :submitted="steps[0].submitted"
                    @update-name="updateName"
                    @update-request-notes="updateRequestNotes"
                    @update-quantity="updateQuantity"
                    @update-timezone="updateTimezone"
                    @update-start-time="updateStartTime"
                    @update-end-time="updateEndTime"
                    @add-time-segment="addTimeSegment"
                    @delete-time-segment="deleteTimeSegment"
                    @copy-time-segment="copyTimeSegment"
                />

                <appointment-request-lists
                    v-if="currentStepIndex === 1"
                    :client-id="clientId"
                    :appointment-request="appointmentRequest"
                    :is-loading-contact-lists="isLoadingContactLists"
                    :contact-lists-filter="contactListsFilter"
                    :contact-lists="contactLists"
                    @update-audience-expansion="updateAudienceExpansion"
                    @filter-contact-lists="filterContactLists"
                    @select-contact-list="selectContactList"
                    @remove-contact-list="removeContactList"
                    @select-all-contact-lists="selectAllContactLists"
                    @remove-all-contact-lists="removeAllSelectedContactLists"
                />

                <appointment-request-review
                    v-if="currentStepIndex === 2"
                    :appointment-request="appointmentRequest"
                    :finance-values="financeValues"
                    :subtotal="subtotal"
                    @edit-section="editSection"
                />

                <appointment-request-payment
                    v-if="currentStepIndex === 3"
                    :$v="$v"
                    :appointment-request="appointmentRequest"
                    :finance-values="financeValues"
                    :payment-methods="paymentMethods"
                    :payment-method="paymentMethod"
                    :tos-agreement="tosAgreement"
                    :subtotal="subtotal"
                    :processing-fee="processingFee"
                    :amount-due="amountDue"
                    :submitted="steps[3].submitted"
                    @update-payment-method="updatePaymentMethod"
                    @update-tos-agreement="updateTosAgreement"
                />

                <appointment-request-success
                    v-if="currentStepIndex === 4"
                    :$v="$v"
                    :appointment-request="appointmentRequest"
                    :finance-values="financeValues"
                    :payment-method="paymentMethod"
                    :subtotal="subtotal"
                    :processing-fee="processingFee"
                    :amount-due="amountDue"
                />
            </div>
        </div>

        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="cancel-button back-button" @click="close">
                    {{ cancelButtonLabel }}
                </app-button>
            </div>

            <div class="action-container">
                <app-button
                    v-show="showBackButton"
                    class="go-back-button secondary"
                    @click="goPreviousStep"
                >
                    <l6-back-svg />
                    <span>Go Back</span></app-button
                >
                <app-button v-show="showNextButton" class="next-button" @click="goNextStep">
                    {{ nextButtonLabel }}
                </app-button>
                <app-button v-show="showPayButton" class="pay-button" @click="pay">Pay</app-button>
            </div>
        </div>
    </div>
</template>

<script>
import {
    hasTimeRanges,
    noTimeRangeOverlap,
} from '@/components/sliding-widgets/appointment-request-widget/appointment-request-widget-validators';

const DEFAULT_START_TIME = '9:00';
const DEFAULT_END_TIME = '17:00';

import { APPOINTMENT_REQUEST_WIDGET_NAME } from '@/components/sliding-widgets/appointment-request-widget/appointment-request-widget';
import EventBus from '@/scripts/event-bus';

import L6BackSvg from '@/components/svg/grid/l6-back-svg.vue';
import WidgetCloseSvg from '@/components/svg/quick-add/widget-close-svg.vue';
import AppUserAuthorizePopup from '@/components/common/app-user-authorize-popup.vue';
import { minValue, numeric, required } from 'vuelidate/lib/validators';
import { AppointmentRequestService } from '@/scripts/services/appointment-request/appointment-request-service';
import LoadingScreenSvg from '@/components/svg/loading-screen-svg.vue';
import AppPopup from '@/components/popups/app-popup.vue';

const AppButton = () => import('@/components/common/app-button.vue');
const AppointmentDetails = () =>
    import('@/components/sliding-widgets/appointment-request-widget/appointment-request-details');
const AppointmentRequestLists = () =>
    import('@/components/sliding-widgets/appointment-request-widget/appointment-request-lists.vue');
const AppointmentRequestReview = () =>
    import(
        '@/components/sliding-widgets/appointment-request-widget/appointment-request-review.vue'
    );

const AppointmentRequestPayment = () =>
    import(
        '@/components/sliding-widgets/appointment-request-widget/appointment-request-payment.vue'
    );
const AppointmentRequestSuccess = () =>
    import(
        '@/components/sliding-widgets/appointment-request-widget/appointment-request-success.vue'
    );

export default {
    name: APPOINTMENT_REQUEST_WIDGET_NAME,

    components: {
        'app-button': AppButton,
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
        'l6-back-svg': L6BackSvg,
        'appointment-request-details': AppointmentDetails,
        'appointment-request-lists': AppointmentRequestLists,
        'appointment-request-review': AppointmentRequestReview,
        'appointment-request-payment': AppointmentRequestPayment,
        'appointment-request-success': AppointmentRequestSuccess,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            steps: [
                {
                    title: 'Request Appointments',
                    nextButtonLabel: 'Next',
                    submitted: false,
                },
                {
                    title: 'Lists',
                    nextButtonLabel: 'Save & Review',
                    submitted: false,
                },
                {
                    title: 'Review',
                    nextButtonLabel: 'Proceed to Payment',
                    showBackButton: false,
                    submitted: false,
                },
                {
                    title: 'Make a Payment',
                    cancelButtonLabel: 'Close',
                    showPayButton: true,
                    submitted: false,
                },
                {
                    title: 'Success!',
                    cancelButtonLabel: 'Close',
                    showBackButton: false,
                    submitted: false,
                },
            ],

            currentStepIndex: 0,
            isLoading: false,
            isLoadingContactLists: false,

            appointmentRequest: {
                name: '',
                requestNotes: '',
                quantity: null,
                availability: {
                    monday: [],
                    tuesday: [],
                    wednesday: [],
                    thursday: [],
                    friday: [],
                    saturday: [],
                    sunday: [],
                },
                excludedContactLists: [],
                timezone: this.$helpers.guessTimezone(),
                audienceExpansion: false,
            },

            contactListsFilter: '',
            contactLists: [],

            tosAgreement: false,

            paymentMethod: null,

            financeValues: {
                feePercentage: 0,
                pricePerAppointmentUSD: 0,
            },
        };
    },

    validations: {
        appointmentRequest: {
            name: {
                required,
            },

            quantity: {
                required,
                numeric,
                minValue: minValue(1),
            },

            availability: {
                hasTimeRanges,

                monday: {
                    noTimeRangeOverlap,
                },

                tuesday: {
                    noTimeRangeOverlap,
                },

                wednesday: {
                    noTimeRangeOverlap,
                },

                thursday: {
                    noTimeRangeOverlap,
                },

                friday: {
                    noTimeRangeOverlap,
                },

                saturday: {
                    noTimeRangeOverlap,
                },

                sunday: {
                    noTimeRangeOverlap,
                },
            },

            timezone: {
                required,
            },

            timezoneOffset: {
                required,
            },
        },

        paymentMethod: {
            required,
        },

        tosAgreement: {
            required: (tosAgreement) => tosAgreement === true,
        },
    },

    computed: {
        accountId() {
            return this.$store.state.context.account.id;
        },

        clientId() {
            return this.$store.state.context.client.id;
        },

        paymentMethods() {
            return this.widget.paymentMethods;
        },

        currentStep() {
            return this.steps[this.currentStepIndex];
        },

        cancelButtonLabel() {
            return this.currentStep.cancelButtonLabel ?? 'Cancel';
        },

        nextButtonLabel() {
            return this.currentStep.nextButtonLabel ?? 'Next';
        },

        showBackButton() {
            return this.currentStepIndex > 0 && (this.currentStep.showBackButton ?? true);
        },

        showPayButton() {
            return this.currentStep.showPayButton ?? false;
        },

        showNextButton() {
            return this.currentStepIndex < this.steps.length - 1 && !this.showPayButton;
        },

        subtotal() {
            return this.appointmentRequest.quantity * this.financeValues.pricePerAppointmentUSD;
        },

        processingFee() {
            return this.subtotal * (this.financeValues.feePercentage / 100);
        },

        amountDue() {
            return this.subtotal + this.processingFee;
        },
    },

    async created() {
        this.isLoading = true;

        try {
            await Promise.all([this.loadContactLists(), this.loadFinanceValues()]);
        } catch (error) {
            this.$helpers.showFailNotification(
                'Failed to load required appointment request values',
                {
                    error,
                },
            );

            this.close();
        } finally {
            this.isLoading = false;
        }
    },

    methods: {
        updatePaymentMethod(paymentMethod) {
            this.paymentMethod = paymentMethod;
        },

        editSection(section) {
            switch (section) {
                case 'details':
                    this.currentStepIndex = 0;
                    break;
                case 'availability':
                    this.currentStepIndex = 0;

                    setTimeout(() => {
                        this.scrollToAvailability();
                    });
                    break;
                case 'lists':
                    this.currentStepIndex = 1;
                    break;
            }
        },

        updateTosAgreement(tosAgreement) {
            this.tosAgreement = tosAgreement;
        },

        updateAudienceExpansion(checked) {
            this.appointmentRequest.audienceExpansion = checked;
        },

        filterContactLists(filter) {
            this.contactListsFilter = filter;
            this.loadContactLists(filter);
        },

        selectAllContactLists(checked) {
            if (checked) {
                this.appointmentRequest.excludedContactLists = [];
            } else {
                this.appointmentRequest.excludedContactLists = [...this.contactLists];
            }
        },

        removeAllSelectedContactLists() {
            this.appointmentRequest.excludedContactLists = [];
        },

        selectContactList(list) {
            const existingListIndex = this.appointmentRequest.excludedContactLists.findIndex(
                (l) => l.id === list.id,
            );

            if (existingListIndex > -1) {
                this.appointmentRequest.excludedContactLists.splice(existingListIndex, 1);
            } else {
                this.appointmentRequest.excludedContactLists.push(list);
            }
        },

        removeContactList(list) {
            const existingListIndex = this.appointmentRequest.excludedContactLists.findIndex(
                (l) => l.id === list.id,
            );

            if (existingListIndex > -1) {
                this.appointmentRequest.excludedContactLists.splice(existingListIndex, 1);
            }
        },

        async loadContactLists(filter) {
            this.isLoadingContactLists = true;

            return AppointmentRequestService.getContactLists(this.clientId, {
                name: filter,
            })
                .then((res) => {
                    this.contactLists = (res.data.value ?? []).map((list) => ({
                        id: list.Id,
                        label: `${list.Name} (${list.SubscriberCount})`,
                    }));
                })
                .finally(() => {
                    this.isLoadingContactLists = false;
                });
        },

        loadFinanceValues() {
            return AppointmentRequestService.getFinanceValues().then((response) => {
                const data = response.data.data;
                this.financeValues = {
                    feePercentage: data.feePercentage,
                    pricePerAppointmentUSD: data.pricePerAppointmentUSD,
                };
            });
        },

        goPreviousStep() {
            if (this.currentStepIndex <= 0) {
                return;
            }

            this.currentStepIndex--;
        },

        goNextStep() {
            if (this.currentStepIndex >= this.steps.length - 1) {
                return;
            }

            this.steps[this.currentStepIndex].submitted = true;

            if (this.isStepInvalid()) {
                this.scrollToError();
                return;
            }

            this.currentStepIndex++;

            if (this.currentStepIndex === 1 && this.contactListsFilter) {
                this.contactListsFilter = '';
                this.loadContactLists();
            }
        },

        isStepInvalid() {
            switch (this.currentStepIndex) {
                case 0:
                    return (
                        this.$v.appointmentRequest.name.$invalid ||
                        this.$v.appointmentRequest.quantity.$invalid ||
                        this.$v.appointmentRequest.availability.$invalid
                    );
                case 3:
                    return this.$v.paymentMethod.$invalid || this.$v.tosAgreement.$invalid;
            }

            return false;
        },

        pay() {
            this.steps[3].submitted = true;

            if (this.isStepInvalid()) {
                this.scrollToError();
                return;
            }

            this.$modal.show(
                AppPopup,
                {
                    title: 'Re-enter Password',
                    element: {
                        name: AppUserAuthorizePopup.name,
                        description: 'Enter your password to authorize the payment.',
                        email: this.$store.state.user.email,
                        onAuthorize: this.createAppointment,
                        authorizeButtonLabel: 'Authorize Payment',
                    },
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                },
            );
        },

        createAppointment(email, password) {
            const timezone = this.appointmentRequest.timezone.name;
            const timezoneOffset = this.$moment.tz(new Date(), timezone).format('Z');

            const appointmentRequest = {
                ...this.appointmentRequest,
                excludedContactLists: this.appointmentRequest.excludedContactLists.map(
                    (list) => list.id,
                ),
                timezone,
                timezoneOffset,
            };

            const paymentMethodId = this.paymentMethod.paymentMethodId;

            return AppointmentRequestService.createAppointment(
                this.accountId,
                appointmentRequest,
                paymentMethodId,
                password,
            ).then(() => {
                this.goNextStep();
                EventBus.$emit('appointment-request-filter:update');
            });
        },

        close() {
            EventBus.$emit('close-widget', this.widget);
        },

        updateName(name) {
            this.appointmentRequest.name = name;
        },

        updateRequestNotes(requestNotes) {
            this.appointmentRequest.requestNotes = requestNotes;
        },

        updateQuantity(amount) {
            this.appointmentRequest.quantity = amount;
        },

        updateTimezone(timezone) {
            this.appointmentRequest.timezone = { ...timezone };
        },

        updateStartTime({ day, index, time }) {
            this.appointmentRequest.availability[day][index].start = time;
        },

        updateEndTime({ day, index, time }) {
            this.appointmentRequest.availability[day][index].end = time;
        },

        addTimeSegment(day) {
            this.appointmentRequest.availability[day].push({
                start: DEFAULT_START_TIME,
                end: DEFAULT_END_TIME,
            });
        },

        deleteTimeSegment({ day, index }) {
            this.appointmentRequest.availability[day].splice(index, 1);
        },

        copyTimeSegment({ timeRange, days }) {
            Object.keys(days).forEach((day) => {
                if (days[day]) {
                    this.appointmentRequest.availability[day].push({
                        start: timeRange.start.value,
                        end: timeRange.end.value,
                    });
                }
            });
        },

        scrollToError() {
            this.scrollTo('.appointment-error');
        },

        scrollToAvailability() {
            this.scrollTo('.availability-table');
        },

        scrollTo(selector) {
            const scrollContainer = this.$refs.contentWrapper;
            const element = scrollContainer.querySelector(selector);

            if (element) {
                const offset = 24;
                const widgetHeaderHeight = 67;
                const top = element.offsetTop - widgetHeaderHeight - offset;

                scrollContainer.scrollTo({ top, behavior: 'smooth' });
            }
        },
    },
};
</script>

<style>
.appointment-request-widget .loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
}

.appointment-request-widget .request-link {
    color: var(--color-text-link);
}

.appointment-request-widget .divider {
    margin: 24px 0;
    border-bottom: 1px solid var(--color-border-divider);
}

.appointment-request-widget .action-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
}

.appointment-request-widget .go-back-button.secondary {
    display: flex;
    align-items: center;
    gap: 8px;
}

.appointment-request-widget .go-back-button.secondary .l6-back-svg {
    width: 12px;
}

.appointment-request-widget .go-back-button.secondary .l6-back-svg path {
    fill: currentColor;
    transition: 250ms ease-in fill;
}

.appointment-request-widget .go-back-button.secondary:hover {
    background: var(--color-background-button-primary);
    color: var(--color-text-inverse);
}

.appointment-request-widget .go-back-button.secondary:hover .l6-back-svg path {
    fill: var(--color-text-inverse);
}

.appointment-request-widget .pay-button,
.appointment-request-widget .pay-button:hover {
    background: linear-gradient(
        to top,
        var(--color-fill-accent-limegreen) 0%,
        var(--color-fill-accent-other-mar) 100%
    );
}

.appointment-request-widget .appointment-error-message {
    color: var(--color-text-warning);
    font-size: 12px;
}
</style>
